import { ApprovedFieldProjection } from "@/api/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { EmptyState, PageHeader, Pill } from "./shared";

function importanceTone(value: string): "green" | "amber" | "slate" {
  if (value.toLowerCase() === "high") return "amber";
  if (value.toLowerCase() === "medium") return "slate";
  return "green";
}

export function ApprovedFieldsSection({
  fields,
  onCreate,
  onUsage,
  onRetire,
  retireReason,
  onRetireReasonChange,
}: {
  fields: ApprovedFieldProjection[];
  onCreate: () => void;
  onUsage: (field: ApprovedFieldProjection) => void;
  onRetire: (field: ApprovedFieldProjection) => void;
  retireReason: string;
  onRetireReasonChange: (reason: string) => void;
}) {
  const grouped = fields.reduce<Map<string, ApprovedFieldProjection[]>>((map, field) => {
    const category = field.category || "Other";
    map.set(category, [...(map.get(category) ?? []), field]);
    return map;
  }, new Map());

  return (
    <>
      <PageHeader
        title="Approved application fields"
        subtitle="Governance defines the field standards. PMs choose approved fields when building scholarship forms."
        action={<Button onClick={onCreate}>Create approved field</Button>}
      />
      <div className="space-y-4 p-4">
        {fields.length === 0 ? (
          <EmptyState
            title="No approved fields loaded"
            body="Approved fields will appear here once the canonical field library API returns active fields."
            action={<Button onClick={onCreate}>Create approved field</Button>}
          />
        ) : null}

        <label className="block rounded-lg border bg-white p-4 text-sm font-medium">
          Reason for retiring fields
          <div className="mt-1 text-xs font-normal text-gray-500">
            Required before retiring an approved field. Historical forms are never changed.
          </div>
          <Input
            className="mt-2"
            value={retireReason}
            onChange={(event) => onRetireReasonChange(event.target.value)}
            placeholder="Example: Unused after program review"
          />
        </label>

        {Array.from(grouped.entries()).map(([category, categoryFields]) => (
          <div key={category} className="overflow-hidden rounded-lg border bg-white">
            <div className="border-b bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              {category}
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Field name</TableHead>
                    <TableHead>Importance</TableHead>
                    <TableHead>Review impact</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categoryFields.map((field) => (
                    <TableRow key={field.field_key}>
                      <TableCell>
                        <div className="font-medium">{field.label}</div>
                        {field.platform_locked ? (
                          <div className="mt-1 text-xs text-gray-500">Platform locked</div>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Pill tone={importanceTone(field.importance)}>
                          {field.importance || "Medium"}
                        </Pill>
                      </TableCell>
                      <TableCell className="max-w-sm text-xs text-gray-600">
                        {field.review_impact || "No additional review"}
                      </TableCell>
                      <TableCell>
                        <Pill tone={field.active ? "green" : "amber"}>
                          {field.active ? "Active" : "Retired"}
                        </Pill>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => onUsage(field)}
                          >
                            Usage
                          </Button>
                          {!field.platform_locked && field.active ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => onRetire(field)}
                            >
                              Retire
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
